import { zodResolver } from '@hookform/resolvers/zod';
import { FieldValues, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Grid2, List, ListItem, Paper, Stack, Typography } from '@mui/material';

import { usePageTitle } from '@enroller/utils';
import { AddressInput, FormField, FormFieldType, API_FIELDS_CONTACT as CONTACT, API_FIELDS_DEAL as DEAL } from '@enroller/admissions-components';

import { getConfig } from '../../../utils/config';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { countryOptions, genderOptions, programOptions } from '../../../constants';
import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

import type { StudentApplicationValues } from '../StudentApplication';

import {
  defaultValues,
  studentDetailsSchema,
  createStudentDetailsPayload,
  CURRENT_ADDRESS_FIELDS_NAME,
  type StudentDetailsFormValues,
  PAGE_SECTIONS,
} from './StudentDetailsFormValues';

const studentAddressInputs: AddressInput[] = [
  { name: CONTACT.addressLine1, label: 'Current Address Line 1', required: true },
  { name: CONTACT.addressLine2, label: 'Current Address Line 2' },
  { name: CONTACT.city, label: 'City', required: true },
  { name: CONTACT.postalCode, label: 'Postal Code', required: true },
  { name: CONTACT.country, label: 'Country', required: true },
];

interface StudentDetailsFormProps {
  modulesID?: StudentApplicationValues['modulesID'];
  studentDetails?: StudentApplicationValues['studentDetails'];
  submitApplication: (data: FieldValues) => Promise<void>;
}

export const StudentDetailsForm = ({ modulesID, studentDetails, submitApplication }: StudentDetailsFormProps) => {
  const { googleMapsAPIKey } = getConfig();
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'Student Details', subtitle: 'Personal information' });
    setPageTitle('Student Details');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(
    () => zodResolver(validationMode === ValidationType.Full ? studentDetailsSchema.required : studentDetailsSchema.optional),
    [validationMode],
  );

  const { control, handleSubmit, reset, trigger } = useForm<StudentDetailsFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  useEffect(() => {
    if (studentDetails) {
      reset({
        ...defaultValues,
        ...studentDetails,
      });
    }
  }, [studentDetails, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        setApplicationPageData('studentDetails', data);
        if (!modulesID) return;
        setIsFormLoading(true);

        const apiPayload = createStudentDetailsPayload(data, modulesID);

        submitApplication(apiPayload)
          .then(() => console.log('[SAP] Student Details API: submit success'))
          .catch((error) => console.error('[SAP] Student Details API: submit error:', error))
          .finally(() => setIsFormLoading(false));
      })(),
    [handleSubmit, modulesID, setIsFormLoading, setApplicationPageData, submitApplication],
  );

  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Personal Information</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">Name Requirements:</Typography>
              <List sx={{ listStyleType: 'disc', pl: 3, py: 0, '& .MuiListItem-root': { display: 'list-item', p: 0 } }}>
                <ListItem>Latin (Roman) alphabet characters only with no diacritical marks</ListItem>
                <ListItem>No hyphens</ListItem>
                <ListItem>No apostrophes</ListItem>
                <ListItem>One space between names</ListItem>
              </List>
            </Alert>
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              type={FormFieldType.Text}
              label="First/Middle Names (as printed on passport)"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.firstName}`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              type={FormFieldType.Text}
              label="Last Name (as printed on passport)"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.lastName}`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              type={FormFieldType.Text}
              label="Preferred Name"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.preferredName}`}
              control={control}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Program"
              name={`${PAGE_SECTIONS.personalInformation}.${DEAL.programType}`}
              control={control}
              options={programOptions}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              type={FormFieldType.Radio}
              label="Gender"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.gender}`}
              control={control}
              options={genderOptions}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Date} label="Date of Birth" name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.dateOfBirth}`} control={control} />
          </Grid2>
          <Grid2 size={4} />
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="City of Birth" name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.cityOfBirth}`} control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              label="Country of Birth"
              options={countryOptions}
              type={FormFieldType.Dropdown}
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.countryOfBirth}`}
            />
          </Grid2>
          <Grid2 size={4} />
          <Grid2 size={4}>
            <FormField
              control={control}
              options={countryOptions}
              type={FormFieldType.Dropdown}
              label="Country of Legal Permanent Residence"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.residence}`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              options={countryOptions}
              type={FormFieldType.Dropdown}
              label="Country of Citzenship (Passport Country)"
              name={`${PAGE_SECTIONS.personalInformation}.${CONTACT.citizenship}`}
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Contact Information</Typography>
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Email Address" name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.email}`} control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Cell Phone Number" name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.mobile}`} control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              type={FormFieldType.Text}
              label="Home Phone Number"
              name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.homePhone}`}
              control={control}
            />
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              P.O. Boxes are not acceptable for physical address.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.AddressAutocomplete}
              label="Current Address"
              name={`${PAGE_SECTIONS.contactInformation}.${CURRENT_ADDRESS_FIELDS_NAME}`}
              control={control}
              googleMapsAPIKey={googleMapsAPIKey}
              addressInputs={studentAddressInputs}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              type={FormFieldType.Text}
              label="Emergency Contact Name"
              name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.emergencyContactName}`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              type={FormFieldType.Text}
              label="Relation to Applicant (e.g. Father / Mother)"
              name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.emergencyContactRelation}`}
            />
          </Grid2>
          <Grid2 size={4}>
            <FormField
              control={control}
              type={FormFieldType.Text}
              label="Emergency Contact Phone Number"
              name={`${PAGE_SECTIONS.contactInformation}.${CONTACT.emergencyContactPhoneNumber}`}
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Your Photo</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              The photo should be high quality image and resolution that clearly shows the students face and head. Student should not be wearing a hat,
              sunglasses, or anything that obscures their face. The student should also be smiling and the photo taken from a level angle.
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField id="applicantPhoto" label="" control={control} type={FormFieldType.PictureUploader} name={`${PAGE_SECTIONS.yourPhoto}.applicantPhoto`} />
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
